import { motion } from "framer-motion";
import { Link } from "react-router";
import { Users, MessageCircle, Tag, Sparkles, ArrowRight } from "lucide-react";
import { brandColors as colors, usePalette } from "@/lib/palette";
import { fonts } from "@/lib/fonts";
import { fadeUp, stagger, scaleIn } from "@/lib/animations";
import Blob from "@/components/ui/Blob";

export default function CommunitySection() {
  const theme = usePalette();
  return (
    <section id='community' className='py-24 px-6 relative overflow-hidden' style={{ backgroundColor: theme.accentSoft }}>
      <Blob className='w-72 h-72 -top-20 right-10' color={theme.blob2} />

      <div className='max-w-6xl mx-auto relative z-10'>
        <motion.div
          initial='hidden'
          whileInView='visible'
          viewport={{ once: true, margin: "-80px" }}
          variants={stagger}
          className='grid lg:grid-cols-2 gap-16 items-center'
        >
          {/* Cards side */}
          <motion.div variants={fadeUp} custom={0} className='grid grid-cols-2 gap-4'>
            {[
              {
                icon: <Users size={28} />,
                label: "Sew-Alongs",
                color: theme.accent,
                bg: `${theme.accent}18`,
              },
              {
                icon: <Tag size={28} />,
                label: "Member Discounts",
                color: theme.pop,
                bg: `${theme.pop}20`,
              },
              {
                icon: <MessageCircle size={28} />,
                label: "Virtual Space",
                color: theme.warm,
                bg: `${theme.warm}18`,
              },
              {
                icon: <Sparkles size={28} />,
                label: "Makers Who Get It",
                color: colors.denimBlue,
                bg: `${colors.denimBlue}20`,
              },
            ].map((item, i) => (
              <motion.div
                key={item.label}
                variants={scaleIn}
                custom={i}
                className='rounded-2xl p-6 text-center group hover:shadow-lg transition-all duration-300 hover:-translate-y-1 cursor-default'
                style={{ backgroundColor: item.bg }}
              >
                <div
                  className='w-14 h-14 rounded-xl flex items-center justify-center mx-auto mb-3 transition-transform duration-300 group-hover:scale-110'
                  style={{
                    backgroundColor: `${item.color}20`,
                    color: item.color,
                  }}
                >
                  {item.icon}
                </div>
                <p
                  className='text-sm tracking-wide'
                  style={{
                    fontFamily: fonts.body,
                    fontWeight: 700,
                    color: colors.navy,
                    letterSpacing: "0.05em",
                  }}
                >
                  {item.label}
                </p>
              </motion.div>
            ))}
          </motion.div>

          {/* Text side */}
          <div>
            <motion.p
              variants={fadeUp}
              custom={0}
              className='text-xs tracking-[0.2em] uppercase mb-3'
              style={{
                fontFamily: fonts.body,
                fontWeight: 700,
                color: theme.accent,
              }}
            >
              Community
            </motion.p>
            <motion.h2
              variants={fadeUp}
              custom={1}
              className='text-4xl lg:text-5xl mb-6 leading-tight'
              style={{
                fontFamily: fonts.logo,
                fontWeight: 400,
                color: colors.navy,
              }}
            >
              More than sewing. <span style={{ fontFamily: fonts.heading, color: theme.warm }}>It&rsquo;s a community.</span>
            </motion.h2>
            <motion.div
              variants={fadeUp}
              custom={2}
              className='space-y-4 leading-relaxed'
              style={{
                fontFamily: fonts.body,
                fontWeight: 500,
                color: `${colors.navy}bb`,
                letterSpacing: "0.03em",
              }}
            >
              <p>
                Whether you&rsquo;re joining from your sewing room or across the country, I built Loose Threads Club as a place to create, connect, and grow
                together.
              </p>
              <p>
                With monthly sew-alongs, pattern and merch discounts, and a virtual space full of makers who just get it, you&rsquo;ll always have a project,
                and people, to come back to.
              </p>
            </motion.div>
            <motion.div variants={fadeUp} custom={3} className='mt-8'>
              <Link
                to='/loose-threads-club'
                className='inline-flex items-center gap-2 px-8 py-3.5 rounded-full text-white cursor-pointer transition-all duration-250 hover:shadow-xl hover:scale-105 active:scale-[0.98]'
                style={{
                  fontFamily: fonts.button,
                  fontWeight: 700,
                  letterSpacing: "0.06em",
                  textTransform: "uppercase" as const,
                  fontSize: "0.75rem",
                  backgroundColor: theme.accent,
                }}
              >
                Learn more about Loose Threads Club
                <ArrowRight size={16} />
              </Link>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
